import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpDown,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  MapPin,
  Phone,
  ReceiptText,
  Search,
  ShieldCheck,
  UserRound,
  Wallet,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  deleteSale,
  getSaleById,
  getSaleItems,
  getSalePayments,
} from "../../../api/salesApi";
import { Link } from "react-router-dom";
import { cn, money, fmtDate, useDebounced } from "../../../components/utils";
import { useAlert } from "../../../context/AlertContext";

/**
 * SaleDetails.jsx — Production‑ready detailed view for a single sale
 * -----------------------------------------------------------------
 * - Reusable UI composed of: Summary header, Retailer card, Tabs (Items, Payments, Notes)
 * - Items + Payments both support server‑side pagination, search, sorting, and CSV export
 * - Responsive, accessible, keyboard friendly, with skeletons & empty states
 * - Plain JSX (no TypeScript), Tailwind for styling, Framer Motion for micro‑animations
 *
 * DATA CONTRACT (suggested)
 * -------------------------
 * fetchSale(saleId): Promise<{ id, invoice_no, date, retailer, totals, status, note }>
 *   - retailer: { id, name, mobile_number, city }
 *   - totals: { items_count, subtotal, discount_amount, total, paid, balance }
 *
 * fetchSaleItems({ saleId, page, pageSize, q, sort }): Promise<{ data: Item[], total }>
 *   - Item: { id, product: { id, brand, name, size_label, case_pack_qty }, quantity, price_per_unit, total_price, offer_applied, notes }
 *
 * fetchPayments({ saleId, page, pageSize, q, sort }): Promise<{ data: Payment[], total }>
 *   - Payment: { id, payment_type, payment_amount, payment_date, reference_no, payment_status, notes }
 */

// -----------------------------
// Primitive components
// -----------------------------
function Pill({ color = "slate", children }) {
  const palette = {
    emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    rose: "bg-rose-100 text-rose-700 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-2xl border px-2.5 py-1 text-xs font-medium",
        palette[color] || palette.slate
      )}
    >
      {children}
    </span>
  );
}
function BadgeStatus({ status }) {
  const map = {
    // Payment statuses
    PAID: { color: "emerald", icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
    PARTIAL: { color: "amber", icon: <Clock className="h-3.5 w-3.5" /> },
    DUE: { color: "rose", icon: <Clock className="h-3.5 w-3.5" /> },
    REFUNDED: { color: "slate", icon: <ShieldCheck className="h-3.5 w-3.5" /> },

    // Delivery statuses
    Pending: { color: "slate", icon: <Clock className="h-3.5 w-3.5" /> },
    Delivered: {
      color: "emerald",
      icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    },
    Cancelled: { color: "rose", icon: <ShieldCheck className="h-3.5 w-3.5" /> },
    Returned: { color: "amber", icon: <ShieldCheck className="h-3.5 w-3.5" /> },
  };

  const it = map[status] || { color: "slate", icon: null };
  return (
    <Pill color={it.color}>
      {it.icon}
      <span>{status}</span>
    </Pill>
  );
}

function KeyValue({ k, v, mono }) {
  return (
    <div className="flex items-center justify-between gap-6 py-1">
      <div className="text-sm text-gray-500">{k}</div>
      <div
        className={cn(
          "text-sm font-medium text-gray-900",
          mono && "font-mono tabular-nums"
        )}
      >
        {v}
      </div>
    </div>
  );
}

function Copyable({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(text));
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch { }
      }}
      className="inline-flex items-center gap-1 rounded-xl border px-2 py-1 text-xs hover:bg-gray-50"
      title="Copy"
    >
      <Copy className="h-3.5 w-3.5" /> {done ? "Copied" : "Copy"}
    </button>
  );
}

function Paginator({ page, pageSize, total, onPageChange, onPageSizeChange }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < pages;
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
      <div className="text-sm text-gray-500">
        Page {page} of {pages} • {total.toLocaleString()} results
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">Rows</label>
        <select
          className="rounded-xl border px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            onClick={() => canPrev && onPageChange(page - 1)}
            disabled={!canPrev}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => canNext && onPageChange(page + 1)}
            disabled={!canNext}
            className="rounded-xl border px-3 py-1.5 bg-white shadow-sm disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ThSortable({ children, active, dir, onToggle }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
    >
      <button
        onClick={onToggle}
        className="inline-flex items-center gap-1 hover:underline"
      >
        {children}
        <ArrowUpDown
          className={cn(
            "h-4 w-4 transition",
            active ? "opacity-100" : "opacity-40"
          )}
        />
        <span className="sr-only">Sort</span>
      </button>
    </th>
  );
}

function EmptyState({
  title = "Nothing here",
  desc = "Try adjusting your search or filters.",
  onClear,
}) {
  return (
    <div className="rounded-2xl border p-10 bg-white text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-indigo-50 flex items-center justify-center">
        <Search className="h-6 w-6 text-indigo-600" />
      </div>
      <h3 className="mt-4 text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{desc}</p>
      {onClear && (
        <div className="mt-4">
          <button
            onClick={onClear}
            className="inline-flex items-center gap-1 rounded-xl border px-3 py-2 shadow-sm hover:bg-gray-50"
          >
            <X className="h-4 w-4" /> Clear
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ title, actions, children }) {
  return (
    <section className="mt-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

// -----------------------------
// Main component
// -----------------------------
export default function SaleDetails({
  saleId,
  fetchSale,
  fetchSaleItems,
  fetchPayments,
  onBack,
}) {
  // Summary
  const [sale, setSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(true);

  // Items query state
  const [itemPage, setItemPage] = useState(1);
  const [itemPageSize, setItemPageSize] = useState(20);
  const [itemQ, setItemQ] = useState("");
  const [itemSort, setItemSort] = useState({ field: "", dir: "asc" });
  const itemQDeb = useDebounced(itemQ, 400);

  // Payments query state
  const [payPage, setPayPage] = useState(1);
  const [payPageSize, setPayPageSize] = useState(10);
  const [payQ, setPayQ] = useState("");
  const [paySort, setPaySort] = useState({ field: "", dir: "desc" });
  const payQDeb = useDebounced(payQ, 400);

  // Items data
  const [items, setItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [loadingItems, setLoadingItems] = useState(false);

  // Payments data
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(false);

  // Tabs
  const [tab, setTab] = useState("items"); // items | payments | notes

  // Load sale summary
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingSale(true);
      try {
        const r = await fetchSale(saleId);
        if (alive) setSale(r);
      } finally {
        if (alive) setLoadingSale(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [saleId, fetchSale]);

  // Load items
  const itemQuery = useMemo(
    () => ({
      saleId,
      page: itemPage,
      pageSize: itemPageSize,
      q: itemQDeb || undefined,
      sort: itemSort.field ? itemSort : undefined,
    }),
    [saleId, itemPage, itemPageSize, itemQDeb, itemSort]
  );
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingItems(true);
      try {
        const r = await fetchSaleItems(itemQuery);
        if (alive) {
          setItems(r.data || []);
          setItemsTotal(r.total || 0);
        }
      } finally {
        if (alive) setLoadingItems(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    fetchSaleItems,
    itemQuery.page,
    itemQuery.pageSize,
    itemQuery.q,
    itemQuery.sort && itemQuery.sort.field,
    itemQuery.sort && itemQuery.sort.dir,
  ]);

  // Load payments
  const payQuery = useMemo(
    () => ({
      saleId,
      page: payPage,
      pageSize: payPageSize,
      q: payQDeb || undefined,
      sort: paySort.field ? paySort : undefined,
    }),
    [saleId, payPage, payPageSize, payQDeb, paySort]
  );

  const { showAlert } = useAlert();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingPayments(true);
      try {
        const r = await fetchPayments(payQuery);
        if (alive) {
          setPayments(r.data || []);
          setPaymentsTotal(r.total || 0);
        }
      } finally {
        if (alive) setLoadingPayments(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [
    fetchPayments,
    payQuery.page,
    payQuery.pageSize,
    payQuery.q,
    payQuery.sort && payQuery.sort.field,
    payQuery.sort && payQuery.sort.dir,
  ]);

  function toggleItemSort(field) {
    setItemSort((prev) =>
      prev.field !== field
        ? { field, dir: "asc" }
        : { field, dir: prev.dir === "asc" ? "desc" : "asc" }
    );
  }
  function togglePaySort(field) {
    setPaySort((prev) =>
      prev.field !== field
        ? { field, dir: "asc" }
        : { field, dir: prev.dir === "asc" ? "desc" : "asc" }
    );
  }

  async function exportItemsCsv() {
    try {
      const params = new URLSearchParams();
      Object.entries(itemQuery).forEach(([k, v]) => {
        if (!v) return;
        if (k === "sort" && v.field) {
          params.set("sortField", String(v.field));
          params.set("sortDir", v.dir);
        } else if (k !== "sort") {
          params.set(k, String(v));
        }
      });
      params.set("export", "csv");
      const resp = await fetch(
        `/api/sales/${saleId}/items?${params.toString()}`,
        { headers: { Accept: "text/csv" } }
      );
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sale_${saleId}_items.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showAlert({ message: "Export failed", type: "error" });
    }
  }
  async function exportPaymentsCsv() {
    try {
      const params = new URLSearchParams();
      Object.entries(payQuery).forEach(([k, v]) => {
        if (!v) return;
        if (k === "sort" && v.field) {
          params.set("sortField", String(v.field));
          params.set("sortDir", v.dir);
        } else if (k !== "sort") {
          params.set(k, String(v));
        }
      });
      params.set("export", "csv");
      const resp = await fetch(
        `/api/sales/${saleId}/payments?${params.toString()}`,
        { headers: { Accept: "text/csv" } }
      );
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sale_${saleId}_payments.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showAlert({ message: "Export failed", type: "error" });
    }
  }

  async function handleDeleteSale() {
    if (!sale) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete sale ${sale.invoice_no}? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteSale(saleId); // Call backend API
      showAlert({
        message: `Sale ${sale.invoice_no} has been deleted successfully.`,
        type: "success",
      });
      // Redirect back to sales list or dashboard
      if (onBack) onBack();
    } catch (err) {
      console.error(err);
      showAlert({ message: "Failed to delete sale.", type: "error" });
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 shadow-sm hover:bg-gray-50"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <ReceiptText className="h-4 w-4" />
                <span>{sale ? sale.invoice_no : "Loading…"}</span>
                {sale && <Copyable text={sale.invoice_no} />}
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                Sale Details
              </h1>
              <div className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                <Calendar className="h-4 w-4" />{" "}
                {sale ? fmtDate(sale.date) : "—"}
                {sale?.status && <BadgeStatus status={sale.status} />}
                {sale?.delivery_status && (
                  <BadgeStatus status={sale.delivery_status} />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
              onClick={() => window.print()}
            >
              <Download className="h-4 w-4" /> Print / Save
            </button>
            {sale?.retailer?.mobile_number && (
              <a
                href={`tel:${sale.retailer.mobile_number}`}
                className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
              >
                <Phone className="h-4 w-4" /> Call
              </a>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-2xl border border-gray-300 p-4 bg-white">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Retailer
            </div>
            <div className="mt-2 flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-gray-900 flex items-center gap-4 capitalize">
                  <UserRound className="h-4 w-4" />{" "}
                  {sale?.retailer?.name || "-"}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-4">
                  <Phone className="h-4 w-4" />{" "}
                  {sale?.retailer?.mobile_number || "-"}
                </div>
                <div className="text-sm text-gray-500 flex items-center gap-4 capitalize">
                  <MapPin className="h-4 w-4" /> {sale?.retailer?.city || "-"}
                </div>
              </div>
              {sale?.retailer?.id && (
                <a
                  className="inline-flex items-center gap-1 text-indigo-600 text-sm hover:underline"
                  href={`/retailers/${sale.retailer.id}`}
                >
                  <ExternalLink className="h-4 w-4" /> View
                </a>
              )}
            </div>
          </div>

          <div className="rounded-2xl border p-4 border-gray-300 bg-white">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Totals
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <KeyValue k="Items" v={sale?.totals?.items_count ?? "-"} />
              <KeyValue k="Subtotal" v={money(sale?.totals?.subtotal)} mono />
              <KeyValue
                k="Discount"
                v={money(sale?.totals?.discount_amount)}
                mono
              />
              <KeyValue k="Total" v={money(sale?.totals?.total)} mono />
              <KeyValue k="Paid" v={money(sale?.totals?.paid)} mono />
              <KeyValue k="Balance" v={money(sale?.totals?.balance)} mono />
            </div>
          </div>

          <div className="rounded-2xl border p-4 border-gray-300 bg-white">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Quick Actions
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <Link
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                to={`/invoices/${saleId}`}
              >
                <ReceiptText className="h-4 w-4" /> Invoice
              </Link>
              {sale?.status != "PAID" && sale?.delivery_status != "Returned"  && (
                <Link
                  className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm text-white shadow-sm bg-green-600 hover:bg-green-700"
                  to={`/sale/${saleId}/collect`}
                >
                  <Wallet className="h-4 w-4" /> Collect Payment
                </Link>
              )}

              {sale?.delivery_status != "Returned" && (
                <Link
                  className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm hover:bg-gray-50"
                  to={`/sale/${saleId}/return`}
                >
                  <CreditCard className="h-4 w-4" /> Return / Refund
                </Link>
              )}
              {/* Delete Sale Button */}
              <button
                onClick={handleDeleteSale}
                className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm shadow-sm bg-red-600 text-white hover:bg-red-700"
              >
                <X className="h-4 w-4" /> Delete Sale
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b-1 border-gray-300 flex items-center gap-4">
          {[
            { id: "items", label: "Items" },
            { id: "payments", label: "Payments" },
            { id: "notes", label: "Notes" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "px-2 py-2 -mb-px border-b-2",
                tab === t.id
                  ? "border-rose-600 text-rose-700 font-semibold"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Items */}
        {tab === "items" && (
          <Section
            title="Line Items"
            actions={
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    value={itemQ}
                    onChange={(e) => {
                      setItemQ(e.target.value);
                      setItemPage(1);
                    }}
                    placeholder="Search product, brand, size, offer…"
                    className="w-56 rounded-2xl border bg-white border-gray-300 pl-9 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2"
                  />
                </div>
                <button
                  onClick={exportItemsCsv}
                  className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" /> Export
                </button>
              </>
            }
          >
            <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-300 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <ThSortable
                        active={itemSort.field === "brand"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("brand")}
                      >
                        Brand
                      </ThSortable>
                      <ThSortable
                        active={itemSort.field === "name"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("name")}
                      >
                        Product
                      </ThSortable>
                      <ThSortable
                        active={itemSort.field === "size_label"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("size_label")}
                      >
                        Size
                      </ThSortable>
                      <ThSortable
                        active={itemSort.field === "quantity"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("quantity")}
                      >
                        Qty
                      </ThSortable>
                      <ThSortable
                        active={itemSort.field === "price_per_unit"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("price_per_unit")}
                      >
                        Unit Price
                      </ThSortable>
                      <ThSortable
                        active={itemSort.field === "total_price"}
                        dir={itemSort.dir}
                        onToggle={() => toggleItemSort("total_price")}
                      >
                        Total
                      </ThSortable>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Offer
                      </th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loadingItems && (
                      <tr>
                        <td
                          colSpan={8}
                          className="p-6 text-center text-sm text-gray-500"
                        >
                          <Loader2 className="inline-block h-4 w-4 animate-spin mr-2" />{" "}
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loadingItems && items.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-6">
                          <EmptyState title="No line items" />
                        </td>
                      </tr>
                    )}
                    {!loadingItems &&
                      items.map((it) => (
                        <tr key={it.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {it.product?.brand || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {it.product?.name || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {it.product?.size_label || "-"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {it.quantity} {it.units}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {money(it.price_per_unit)}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">
                            {money(it.total_price)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {it.offer_applied || "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {it.product?.id && (
                              <a
                                href={`/products/${it.product.id}`}
                                className="inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs shadow-sm hover:bg-gray-50"
                              >
                                View <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 pb-3">
                <Paginator
                  page={itemPage}
                  pageSize={itemPageSize}
                  total={itemsTotal}
                  onPageChange={(p) => setItemPage(p)}
                  onPageSizeChange={(n) => {
                    setItemPageSize(n);
                    setItemPage(1);
                  }}
                />
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden grid grid-cols-1 gap-3">
              {loadingItems && (
                <div className="rounded-2xl border p-4 bg-white text-center text-sm text-gray-500">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" />{" "}
                  Loading…
                </div>
              )}
              {!loadingItems && items.length === 0 && (
                <EmptyState title="No line items" />
              )}
              {!loadingItems &&
                items.map((it) => (
                  <div key={it.id} className="rounded-2xl border p-4 bg-white">
                    <div className="font-semibold text-gray-900">
                      {it.product?.brand} — {it.product?.name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {it.product?.size_label || "-"}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      <div className="text-gray-500">Qty</div>
                      <div className="text-right">{it.quantity}</div>
                      <div className="text-gray-500">Unit</div>
                      <div className="text-right">
                        {money(it.price_per_unit)}
                      </div>
                      <div className="text-gray-900 font-semibold">Total</div>
                      <div className="text-right font-semibold">
                        {money(it.total_price)}
                      </div>
                    </div>
                    {it.offer_applied && (
                      <div className="text-xs mt-2 text-indigo-700">
                        Offer: {it.offer_applied}
                      </div>
                    )}
                  </div>
                ))}
              {items.length > 0 && (
                <Paginator
                  page={itemPage}
                  pageSize={itemPageSize}
                  total={itemsTotal}
                  onPageChange={(p) => setItemPage(p)}
                  onPageSizeChange={(n) => {
                    setItemPageSize(n);
                    setItemPage(1);
                  }}
                />
              )}
            </div>
          </Section>
        )}

        {/* Payments */}
        {tab === "payments" && (
          <Section
            title="Payments"
            actions={
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    value={payQ}
                    onChange={(e) => {
                      setPayQ(e.target.value);
                      setPayPage(1);
                    }}
                    placeholder="Search reference no, notes, type…"
                    className="w-56 rounded-2xl border bg-white pl-9 pr-3 py-2 shadow-sm focus:outline-none focus:ring-2"
                  />
                </div>
                <button
                  onClick={exportPaymentsCsv}
                  className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-sm hover:bg-gray-50"
                >
                  <Download className="h-4 w-4" /> Export
                </button>
              </>
            }
          >
            <div className="hidden md:block overflow-hidden rounded-2xl border bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <ThSortable
                        active={paySort.field === "payment_date"}
                        dir={paySort.dir}
                        onToggle={() => togglePaySort("payment_date")}
                      >
                        Date
                      </ThSortable>
                      <ThSortable
                        active={paySort.field === "payment_type"}
                        dir={paySort.dir}
                        onToggle={() => togglePaySort("payment_type")}
                      >
                        Type
                      </ThSortable>
                      <ThSortable
                        active={paySort.field === "payment_amount"}
                        dir={paySort.dir}
                        onToggle={() => togglePaySort("payment_amount")}
                      >
                        Amount
                      </ThSortable>
                      <ThSortable
                        active={paySort.field === "payment_status"}
                        dir={paySort.dir}
                        onToggle={() => togglePaySort("payment_status")}
                      >
                        Status
                      </ThSortable>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Reference
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                        Notes
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loadingPayments && (
                      <tr>
                        <td
                          colSpan={6}
                          className="p-6 text-center text-sm text-gray-500"
                        >
                          <Loader2 className="inline-block h-4 w-4 animate-spin mr-2" />{" "}
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loadingPayments && payments.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-6">
                          <EmptyState title="No payments" />
                        </td>
                      </tr>
                    )}
                    {!loadingPayments &&
                      payments.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {fmtDate(p.payment_date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {p.payment_type}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">
                            {money(p.payment_amount)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {p.payment_status}
                          </td>
                          <td className="px-4 py-3 text-sm font-mono">
                            {p.reference_no || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {p.notes || ""}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 pb-3">
                <Paginator
                  page={payPage}
                  pageSize={payPageSize}
                  total={paymentsTotal}
                  onPageChange={(p) => setPayPage(p)}
                  onPageSizeChange={(n) => {
                    setPayPageSize(n);
                    setPayPage(1);
                  }}
                />
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden grid grid-cols-1 gap-3">
              {loadingPayments && (
                <div className="rounded-2xl border p-4 bg-white text-center text-sm text-gray-500">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" />{" "}
                  Loading…
                </div>
              )}
              {!loadingPayments && payments.length === 0 && (
                <EmptyState title="No payments" />
              )}
              {!loadingPayments &&
                payments.map((p) => (
                  <div key={p.id} className="rounded-2xl border p-4 bg-white">
                    <div className="text-sm text-gray-500">
                      {fmtDate(p.payment_date)}
                    </div>
                    <div className="mt-1 font-semibold text-gray-900">
                      {p.payment_type} • {money(p.payment_amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {p.payment_status}
                    </div>
                    {p.reference_no && (
                      <div className="mt-1 text-xs font-mono">
                        Ref: {p.reference_no}
                      </div>
                    )}
                    {p.notes && (
                      <div className="mt-1 text-sm text-gray-700">
                        {p.notes}
                      </div>
                    )}
                  </div>
                ))}
              {payments.length > 0 && (
                <Paginator
                  page={payPage}
                  pageSize={payPageSize}
                  total={paymentsTotal}
                  onPageChange={(p) => setPayPage(p)}
                  onPageSizeChange={(n) => {
                    setPayPageSize(n);
                    setPayPage(1);
                  }}
                />
              )}
            </div>
          </Section>
        )}

        {/* Notes */}
        {tab === "notes" && (
          <Section title="Notes">
            <div className="rounded-2xl border p-4 bg-white text-sm text-gray-700 whitespace-pre-wrap min-h-24">
              {sale?.note || "No notes for this sale."}
            </div>
          </Section>
        )}
      </motion.div>
    </div>
  );
}

// -----------------------------
// fetchers
// -----------------------------
export async function fetchSale(saleId) {
  const r = await getSaleById(saleId);
  return r;
}

export async function fetchSaleItems({ saleId, page, pageSize, q, sort }) {
  const params = {
    page,
    pageSize,
  };

  if (q) params.q = q;
  if (sort?.field) {
    params.sortField = sort.field;
    params.sortDir = sort.dir;
  }

  const r = await getSaleItems(saleId, params);
  return r;
}

export async function fetchPayments({ saleId, page, pageSize, q, sort }) {
  const params = {
    page,
    pageSize,
  };

  if (q) params.q = q;
  if (sort?.field) {
    params.sortField = sort.field;
    params.sortDir = sort.dir;
  }
  const r = await getSalePayments(saleId, params);
  return r;
}
